import type { ReactNode } from "react";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import { usePermission } from "../hooks/use-permission";
import type { UserRole } from "../permissions";
import { usePageLoader } from "@/contexts/page-loader-context";

export interface RoleGuardProps {
	/**
	 * Minimum required role to access the route
	 */
	requiredRole?: UserRole;

	/**
	 * Allowed roles to access the route (if specified, only these exact roles are allowed)
	 */
	allowedRoles?: UserRole[];

	/**
	 * Redirect path when user doesn't have permission
	 * Defaults to reviews
	 */
	redirectTo?: string;

	/**
	 * Optional fallback component to render instead of redirecting
	 */
	fallback?: ReactNode;

	/**
	 * Whether to use Outlet (for route guards) or render children (for component guards)
	 */
	children?: ReactNode;
}

/**
 * Guard component that protects routes based on user roles
 *
 * @example
 * // Require minimum role (admin can access)
 * <Route element={<RoleGuard requiredRole="admin" />}>
 *   <Route path="/settings" element={<SettingsPage />} />
 * </Route>
 *
 * @example
 * // Require specific roles only
 * <Route element={<RoleGuard allowedRoles={["admin"]} />}>
 *   <Route path="/billing" element={<BillingPage />} />
 * </Route>
 *
 * @example
 * // Use as component wrapper
 * <RoleGuard requiredRole="admin" fallback={<AccessDenied />}>
 *   <AdminPanel />
 * </RoleGuard>
 */
export default function RoleGuard({ requiredRole, allowedRoles, redirectTo, fallback, children }: RoleGuardProps) {
	const { role, loading, hasRole } = usePermission();
	const location = useLocation();
	const params = useParams<{ brandUrl: string }>();
	const { showLoader, hideLoader } = usePageLoader();

	// Build the redirect path, defaulting to workspace dashboard if within a workspace route
	const effectiveRedirectTo = redirectTo || (params.brandUrl ? `/${params.brandUrl}/dashboard` : "/brands");

	// Handle loader visibility
	useEffect(() => {
		if (loading) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [loading, showLoader, hideLoader]);

	// Still loading, show nothing (loader is handled by context)
	if (loading) {
		return null;
	}

	// No role found (user might not be a member of workspace)
	if (!role) {
		if (fallback) {
			return <>{fallback}</>;
		}
		return <Navigate to={effectiveRedirectTo} state={{ from: location }} replace />;
	}

	// Check if user has required permissions
	let hasAccess = false;

	if (allowedRoles) {
		// If allowedRoles is specified, check if user's role is in the list
		hasAccess = allowedRoles.includes(role);
	} else if (requiredRole) {
		// If requiredRole is specified, check if user has minimum role
		hasAccess = hasRole(requiredRole);
	} else {
		// If no restrictions specified, allow access
		hasAccess = true;
	}

	// User doesn't have access
	if (!hasAccess) {
		if (fallback) {
			return <>{fallback}</>;
		}
		return <Navigate to={effectiveRedirectTo} state={{ from: location, reason: "insufficient_permissions" }} replace />;
	}

	// Render children if provided, otherwise render Outlet for nested routes
	return children ? <>{children}</> : <Outlet />;
}
